import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import { Chip, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';

import {
  customDateFilter,
  dateRangeOperator,
  serverSideStringFilterOperators,
} from 'portal/src/components/common/Filters/CustomCollectors';
import { CorrectionChip } from 'portal/src/components/common/CorrectionChips';
import addDateColumnProps from 'portal/src/components/common/tables/addDateColumnProps';
import { timestampColumnWidth } from 'portal/src/components/common/tables/columnStyling';

import {
  selectFilterOperators,
  uploadStatusFilterOptions,
  uploadTypeFilterOptions,
} from './UploadHistoryTable.filtering';

import { DataFileTypeChip } from '../FileUploadDetails/components';
import { getFileStatusChipColor } from '../FileUploadDetails/helpers';

type BillUploadHistoryColumnConfig = {
  variant: 'bill';
  isSysAdmin: boolean;
  setShowPicPreview: (statementId: string | null) => void;
  goToStatementDetails: (statementId: string) => void;
};

type SpreadsheetUploadHistoryColumnConfig = {
  variant: 'spreadsheet';
  isSysAdmin: boolean;
  tagConfigs: UploadHistoryTableTagConfigs;
};

type UploadHistoryTableColumnConfig =
  | BillUploadHistoryColumnConfig
  | SpreadsheetUploadHistoryColumnConfig;

export type UploadHistoryTableTagConfigs = {
  tagFieldOptions: { value: string; label: string }[];
  isSavingTag: boolean;
  onSaveTag: (params: { rowId: string; tagValue: string }) => Promise<void>;
};

export const getGridColumns = (
  config: UploadHistoryTableColumnConfig,
): GridColDef[] => {
  const isBillUpload = config.variant === 'bill';

  const uploadTimestampColumn: GridColDef = {
    field: 'uploadTimestamp',
    filterOperators: [...customDateFilter(), dateRangeOperator],
    ...addDateColumnProps(
      'Upload Timestamp',
      timestampColumnWidth,
      'Ppp',
      'left',
    ),
  };

  const uploadTypeColumn: GridColDef = {
    field: 'type',
    headerName: 'Upload Type',
    filterOperators: selectFilterOperators(uploadTypeFilterOptions),
    width: 120,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => <DataFileTypeChip label={params.value} />,
  };

  const originalFileNameColumn: GridColDef = {
    field: 'originalFileName',
    headerName: 'File Name',
    filterOperators: serverSideStringFilterOperators,
    flex: 1,
    minWidth: 200,
    renderCell: ({ value: fileName, row }) => (
      <>
        {isBillUpload && (
          <IconButton
            aria-label="preview statement image"
            onClick={() => config.setShowPicPreview(row.id)}
            size="small"
          >
            <DocumentScannerOutlinedIcon fontSize="small" />
          </IconButton>
        )}
        {fileName}
      </>
    ),
  };

  const statusColumn: GridColDef = {
    field: 'status',
    headerName: 'Status',
    filterOperators: selectFilterOperators(uploadStatusFilterOptions),
    align: 'center',
    minWidth: 220,
    headerAlign: 'center',
    renderCell: (params) => (
      <Chip label={params.value} color={getFileStatusChipColor(params.value)} />
    ),
  };

  const statusTimestampColumn: GridColDef = {
    field: 'statusTimestamp',
    filterOperators: [...customDateFilter(), dateRangeOperator],
    headerName: 'Status Timestamp',
    flex: 1,
    maxWidth: timestampColumnWidth,
    ...addDateColumnProps(
      'Status Timestamp',
      timestampColumnWidth,
      'Ppp',
      'left',
    ),
  };

  const createdByColumn: GridColDef = {
    field: 'createdBy',
    headerName: 'Uploaded By',
    filterOperators: serverSideStringFilterOperators,
    flex: 1,
    maxWidth: 200,
  };

  if (isBillUpload) {
    return [
      uploadTimestampColumn,
      ...(config.isSysAdmin ? [uploadTypeColumn] : []),
      originalFileNameColumn,
      {
        field: 'utilityName',
        headerName: 'Utility',
        filterOperators: serverSideStringFilterOperators,
        flex: 0.5,
        minWidth: 150,
      },
      statusColumn,
      statusTimestampColumn,
      createdByColumn,
      {
        field: 'isCorrection',
        headerName: 'Is Correction',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        display: 'flex',
        filterable: false,
        sortable: false,
        renderCell: ({ value }) => value && <CorrectionChip />,
      },
    ];
  }

  return [
    uploadTimestampColumn,
    uploadTypeColumn,
    originalFileNameColumn,
    {
      field: 'tags',
      type: 'singleSelect',
      editable: true,
      headerName: 'Tag',
      headerAlign: 'center',
      align: 'center',
      filterOperators: selectFilterOperators(config.tagConfigs.tagFieldOptions),
      valueOptions: config.tagConfigs.tagFieldOptions,
      flex: 0.5,
      minWidth: 150,
      renderCell: ({ value }) => <Chip label={value} color="info" />,
    },
    statusColumn,
    statusTimestampColumn,
    createdByColumn,
  ];
};
